interface CookieOptions {
  path?: string
  domain?: string
  expires?: number | Date
  secure?: boolean
  sameSite?: "strict" | "lax" | "none"
}

// Set a cookie with the given name and value
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {}
): void => {
  if (typeof window === "undefined") return

  const opts: CookieOptions = {
    path: "/",
    ...options
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

  if (opts.expires) {
    const expires =
      typeof opts.expires === "number"
        ? new Date(Date.now() + opts.expires * 24 * 60 * 60 * 1000)
        : opts.expires
    cookieString += `; expires=${expires.toUTCString()}`
  }

  if (opts.path) {
    cookieString += `; path=${opts.path}`
  }

  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`
  }

  if (opts.secure) {
    cookieString += "; secure"
  }

  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`
  }

  document.cookie = cookieString
}

// Get a cookie by name
export const getCookie = (name: string): string | null => {
  if (typeof window === "undefined") return null

  const matches = document.cookie.match(
    new RegExp(
      `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1")}=([^;]*)`
    )
  )
  return matches ? decodeURIComponent(matches[1]) : null
}

// Delete a cookie by name
export const deleteCookie = (
  name: string,
  options: CookieOptions = {}
): void => {
  setCookie(name, "", {
    ...options,
    expires: new Date(0)
  })
}

// Check if user has consented to cookies
export const hasConsented = (): boolean => {
  return !!getCookie("cookieConsent") || !!localStorage.getItem("cookieConsent")
}

// Set consent (all or necessary)
export const setConsent = (type: "all" | "necessary"): void => {
  // Store in both cookie and localStorage for redundancy
  setCookie("cookieConsent", type, {
    expires: 365, // Valid for 1 year
    sameSite: "lax",
    secure: window.location.protocol === "https:"
  })

  localStorage.setItem("cookieConsent", type)
}

// Get consent type
export const getConsentType = (): "all" | "necessary" | null => {
  const consent =
    getCookie("cookieConsent") || localStorage.getItem("cookieConsent")
  if (consent === "all" || consent === "necessary") {
    return consent
  }
  return null
}
